import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { resolveMediaUrl, imageVariant } from "@/lib/mediaUrl";
import { singlePhotoHeight } from "@/lib/photoFrame"; // lot 679 — variante choisie sur le plus grand côté affiché
import { ChevronLeft, ChevronRight, Megaphone, X } from "lucide-react";
import { useBackHandler } from "@/lib/backButton";
import { lockBodyScroll } from "@/lib/bodyScrollLock";
import { escapeConsumed } from "@/lib/escapeLayer";
import { isBot } from "@/lib/bots";
import { renderRichText } from "@/components/RichText";
import { ZoomableImage } from "@/components/ZoomableImage";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { avatarSrc } from "@/lib/defaults";
import { timeAgo } from "@/lib/timeAgo";
import { LightboxMenu } from "@/components/LightboxMenu";

export const DEFAULT_PHOTO_CAPTION_CHARS = 120;
/** Admin › Paramètres › Limites — characters shown before « Voir plus » in the photo viewer. */
export const photoCaptionChars = (config) => {
  const n = Number(config?.settings?.photo_caption_chars);
  return Number.isFinite(n) && n >= 40 ? n : DEFAULT_PHOTO_CAPTION_CHARS;
};

/** Facebook-style author row over the photo (avatar · name · « il y a X »), links to the profile / church page. */
export const LightboxAuthor = ({ post }) => {
  const { t } = useApp();
  const author = post.author || {};
  const to = author.is_church && author.church_id ? `/churches/${author.church_id}` : `/profile/${author.id}`;
  const sponsored = !!(post.ad || post.is_ad || post.is_promoted);
  return (
    <div className="min-w-0">
      {sponsored && (
        <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-white/60 mb-1" data-testid="lightbox-sponsored">
          <Megaphone size={11} /> {t("post.sponsored", "Sponsorisé")}
        </div>
      )}
      <Link to={to} className="flex items-center gap-2.5 min-w-0" data-testid="lightbox-author">
        <Avatar className="h-9 w-9 border border-white/30 shrink-0"><AvatarImage src={avatarSrc(author)} /><AvatarFallback>{author.name?.[0]}</AvatarFallback></Avatar>
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-[14px] font-semibold leading-tight"><span className="truncate" data-testid="lightbox-author-name">{author.name}</span><VerifiedBadge user={author} size={13} /></div>
          <div className="text-[12px] text-white/70 leading-tight" data-testid="lightbox-author-time">{timeAgo(post.created_at, t)}</div>
        </div>
      </Link>
    </div>
  );
};

/** Post text over the photo — cut after `maxChars` with « Voir plus » / « Voir moins » (Facebook-style). */
const LightboxCaption = ({ text, maxChars = DEFAULT_PHOTO_CAPTION_CHARS }) => {
  const { t } = useApp();
  const [expanded, setExpanded] = useState(false);
  // Robots : texte complet d'emblée (pas de « Voir plus » à cliquer).
  const long = !isBot() && text.length > maxChars;
  const shown = expanded || !long ? text : `${text.slice(0, maxChars).trimEnd()}…`;
  return (
    <div className="text-[14px] leading-relaxed" data-testid="lightbox-post-text" data-expanded={expanded ? "1" : "0"}>
      <div className={`break-words ${expanded ? "max-h-[38dvh] overflow-y-auto" : ""}`} data-testid="lightbox-post-text-body">
        {renderRichText(shown)}
        {long && !expanded && (
          <>{" "}<button type="button" onClick={() => setExpanded(true)} className="text-[13px] font-semibold text-white/75 hover:text-white transition-colors" data-testid="lightbox-post-text-toggle">{t("nav.see_more", "Voir plus")}</button></>
        )}
      </div>
      {long && expanded && (
        <button type="button" onClick={() => setExpanded(false)} className="mt-1 text-[13px] font-semibold text-white/75 hover:text-white transition-colors" data-testid="lightbox-post-text-toggle">{t("nav.see_less", "Voir moins")}</button>
      )}
    </div>
  );
};

/** Points de pagination façon Instagram : ≤ 5 visibles, fenêtre glissante, points de bord réduits quand la liste continue. */
export const PagerDots = ({ total, index, className = "", style, testId = "lightbox-dots" }) => {
  if (total < 2) return null;
  const MAX = 5;
  const start = total > MAX ? Math.min(Math.max(0, index - 2), total - MAX) : 0;
  const end = Math.min(total, start + MAX);
  const dots = [];
  for (let i = start; i < end; i++) {
    const active = i === index;
    const edge = (i === start && start > 0) || (i === end - 1 && end < total);
    const size = active ? 7 : edge ? 4 : 6;
    dots.push(
      <span key={i} className="rounded-full transition-all duration-200" data-testid={`${testId}-${i}`} data-active={active ? "1" : "0"}
        style={{ width: size, height: size, background: active ? "var(--yalola-primary)" : "rgba(255,255,255,0.45)" }} />
    );
  }
  return (
    <div className={`flex items-center justify-center gap-[5px] ${className}`} style={style} onClick={(e) => e.stopPropagation()} data-testid={testId} data-index={index} data-total={total}>
      {dots}
    </div>
  );
};

const CtlBtn = ({ onClick, className = "", style, testId, children }) => (
  <button onClick={onClick} className={`absolute z-10 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors ${className}`} style={style} data-testid={testId}>{children}</button>
);

/** Fullscreen image viewer with prev/next, counter, keyboard nav.
 *  `author` + `caption` (+ `actions` = reaction row) draw a Facebook-style block over the bottom of the photo.
 *  `comments` (node): TikTok/Reels-style — the photo shrinks into the top band and the comments sheet fills the rest.
 *  `stage` (node): replaces the zoomable image (video viewer) — same shell, single media node. */
export const Lightbox = ({ images = [], index = 0, onClose, onIndex, footer = null, caption = "", author = null, actions = null, maxChars, comments = null, onCommentsClose, stage = null, mediaCaption = "", shareLink = null, downloadUrl = null, downloadKind = "image" }) => {
  const total = images.length;
  const hasBottom = !!footer || !!caption || !!author || !!actions || !!mediaCaption;
  const go = useCallback(
    (d) => onIndex((prev) => (prev + d + total) % total),
    [onIndex, total]
  );

  useEffect(() => {
    const onKey = (e) => {
      const typing = e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName);
      // Une boîte (réactions, republications, partage…) ouverte PAR-DESSUS vient de se fermer sur cet Échap → on reste.
      if (e.key === "Escape") { if (escapeConsumed(e)) return; if (comments) onCommentsClose?.(); else onClose(); }
      else if (typing) return; // saisie en cours (légende, commentaire) : les flèches appartiennent au champ
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    const unlock = lockBodyScroll();
    return () => {
      window.removeEventListener("keydown", onKey);
      unlock();
    };
  }, [go, onClose, comments, onCommentsClose]);

  // Android back button: closes the comments sheet first, then the viewer.
  useBackHandler(true, () => { if (comments) onCommentsClose?.(); else onClose(); return true; });

  // Glisser vers le bas pour fermer (façon Instagram) : le média suit le doigt en rétrécissant, le fond
  // s'éclaircit ; relâché au-delà de 120 px (ou d'un geste rapide) → fermeture, sinon retour élastique.
  const [dragY, setDragY] = useState(0);
  const [settling, setSettling] = useState(false);
  const dragRef = useRef(null);
  const canDrag = !comments;
  const onDragDown = useCallback((dy) => { setSettling(false); setDragY(dy); }, []);
  const onDragEnd = useCallback((dy, ms) => {
    const fast = dy > 40 && dy / Math.max(1, ms) > 0.7;
    if (dy > 120 || fast) { onClose(); return; }
    setSettling(true); setDragY(0);
  }, [onClose]);
  // Vidéo (stage natif) : les pointer events remontent depuis <video> jusqu'ici. Les boutons (vitesse, plein écran)
  // et leurs menus ne démarrent jamais le geste : un enfant qui stoppe le pointerup laisserait sinon un geste orphelin.
  const onStagePointerDown = (e) => {
    if (!canDrag || !stage || e.pointerType === "mouse") return;
    if (e.target instanceof Element && e.target.closest("button, [role=menu]")) return;
    dragRef.current = { x0: e.clientX, y0: e.clientY, t0: Date.now(), active: false };
  };
  const onStagePointerMove = (e) => {
    const g = dragRef.current; if (!g) return;
    const dx = e.clientX - g.x0, dy = e.clientY - g.y0;
    // Balayage horizontal sur une vidéo (album) → média précédent / suivant ; vertical → glisser pour fermer.
    if (!g.active) { if (dy > 12 && dy > Math.abs(dx) * 1.2 && !g.horiz) g.active = true; else if (Math.abs(dx) > 12) { g.horiz = true; return; } else return; }
    if (g.horiz) return;
    onDragDown(Math.max(0, dy));
  };
  const onStagePointerUp = (e) => {
    const g = dragRef.current; dragRef.current = null; if (!g) return;
    if (g.horiz) { const dx = e.clientX - g.x0; if (total > 1 && Math.abs(dx) > 60 && Math.abs(e.clientY - g.y0) < Math.abs(dx)) go(dx < 0 ? 1 : -1); return; }
    if (!g.active) return;
    onDragEnd(Math.max(0, e.clientY - g.y0), Date.now() - g.t0);
  };
  const progress = Math.min(1, dragY / 300);
  const dragStyle = dragY > 0 || settling
    ? { transform: `translate3d(0, ${dragY}px, 0) scale(${1 - progress * 0.22})`, transition: settling ? "transform .28s cubic-bezier(.2,.8,.2,1)" : "none" }
    : undefined;

  if (total === 0) return null;
  const src = resolveMediaUrl(images[index]);
  // Bloc du bas EN FLUX sous le média (vidéo : jamais par-dessus les contrôles natifs ; photo : image centrée dans
  // l'espace libre AU-DESSUS de la description, plus jamais collée à elle). Le recouvrement ne reste que sans bloc du bas.
  const inFlow = !comments && (!!stage || hasBottom);

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center float-up pointer-events-auto ${comments || inFlow ? "justify-start" : "justify-center"}`}
      style={{ background: `rgba(0,0,0,${1 - progress * 0.75})`, transition: settling ? "background .28s" : "none" }}
      data-testid="lightbox"
      data-comments={comments ? "1" : undefined}
      data-dragging={dragY > 0 ? "1" : undefined}
      onClick={comments ? undefined : onClose}
    >
      {total > 1 && (
        <div className="absolute z-10 left-1/2 -translate-x-1/2 text-white/80 text-sm font-semibold" style={{ top: "calc(var(--sa-top, 0px) + 1.25rem)" }} data-testid="lightbox-counter">
          {index + 1} / {total}
        </div>
      )}
      {/* Façon Facebook : croix à GAUCHE, menu « ⋯ » (télécharger, copier le lien) à DROITE. */}
      {/* stopPropagation : sinon le clic remonte au fond noir (onClick={onClose}) → onClose appelé 2 fois → 2 retours arrière. */}
      <CtlBtn onClick={(e) => { e.stopPropagation(); onClose(); }} className="h-10 w-10" style={{ top: "calc(var(--sa-top, 0px) + 1rem)", left: "calc(var(--sa-left, 0px) + 1rem)", opacity: 1 - progress }} testId="lightbox-close"><X size={22} /></CtlBtn>
      {!comments && <LightboxMenu shareLink={shareLink} downloadUrl={downloadUrl} downloadKind={downloadKind} style={{ top: "calc(var(--sa-top, 0px) + 1rem)", right: "calc(var(--sa-right, 0px) + 1rem)", opacity: 1 - progress }} />}
      {/* Téléphone : plus de flèches (balayage au doigt + points façon Instagram) ; PC : flèches conservées. */}
      {total > 1 && !comments && (
        <div className="hidden md:contents">
          <CtlBtn onClick={(e) => { e.stopPropagation(); go(-1); }} className="left-3 md:left-6 top-1/2 -translate-y-1/2 h-11 w-11" testId="lightbox-prev"><ChevronLeft size={26} /></CtlBtn>
          <CtlBtn onClick={(e) => { e.stopPropagation(); go(1); }} className="right-3 md:right-6 top-1/2 -translate-y-1/2 h-11 w-11" testId="lightbox-next"><ChevronRight size={26} /></CtlBtn>
        </div>
      )}
      {/* ONE stage node in both layouts: a playing video must not remount when the comments sheet opens. */}
      {/* Mode commentaires : bande de 38dvh ET overflow-hidden — clavier ouvert, dvh rétrécit ; un cadre 4:3 (texte sur fond)
          plus haut que la bande passait PAR-DESSUS la feuille de commentaires (élément positionné). */}
      <div
        className={`flex items-center justify-center transition-[height,width] duration-300 ${comments ? "shrink-0 w-full px-3 overflow-hidden" : inFlow ? "flex-1 min-h-[28dvh] w-full px-3" : "shrink-0 w-[92vw]"}`}
        style={comments
          ? { height: "38dvh", paddingTop: "var(--sa-top, 0px)" }
          : inFlow
            ? { paddingTop: "calc(var(--sa-top, 0px) + 4rem)", paddingBottom: "0.75rem" }
            : { height: hasBottom ? "calc(72dvh - var(--sa-top, 0px))" : "calc(90dvh - var(--sa-top, 0px) - var(--sa-bottom, 0px))" }}
        onClick={comments ? () => onCommentsClose?.() : (e) => e.stopPropagation()}
        onPointerDown={onStagePointerDown}
        onPointerMove={onStagePointerMove}
        onPointerUp={onStagePointerUp}
        onPointerCancel={onStagePointerUp}
        onTransitionEnd={() => setSettling(false)}
        data-testid="lightbox-stage"
      >
        <div className="w-full h-full flex items-center justify-center" style={{ ...dragStyle, touchAction: canDrag && stage ? "none" : undefined }} data-testid="lightbox-drag">
          {stage || <ZoomableImage key={images[index]} src={src} testId="lightbox-image" onSwipe={total > 1 ? go : undefined} onDragDown={canDrag ? onDragDown : undefined} onDragEnd={canDrag ? onDragEnd : undefined} />}
        </div>
      </div>
      {total > 1 && !comments && (
        <PagerDots total={total} index={index} className="md:hidden shrink-0 py-2" style={{ opacity: 1 - progress }} />
      )}
      {!comments && hasBottom && (
        <div
          className={`w-[min(92vw,560px)] flex flex-col items-center gap-2 ${inFlow ? "shrink min-h-0 overflow-y-auto" : "absolute left-1/2 -translate-x-1/2"}`}
          style={{ ...(inFlow
            ? { marginBottom: "calc(var(--sa-bottom, 0px) + 1rem)" }
            : { bottom: "calc(var(--sa-bottom, 0px) + 1rem)", maxHeight: "calc(100dvh - var(--sa-top, 0px) - var(--sa-bottom, 0px) - 5rem)" }), opacity: 1 - progress, pointerEvents: dragY > 0 ? "none" : undefined }}
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-footer"
        >
          {(author || caption || actions || mediaCaption) && (
            <div className="w-full rounded-lg bg-black/55 backdrop-blur px-3 py-2 text-white text-left space-y-1.5" data-testid="lightbox-post-block">
              {/* Légende propre au média affiché (album) : juste sous le média, au-dessus de l'auteur et du texte. */}
              {mediaCaption && (
                <div key={`mc-${index}`} className="text-sm leading-snug whitespace-pre-wrap pb-1.5 border-b border-white/15" data-testid="lightbox-media-caption" data-index={index}>{mediaCaption}</div>
              )}
              {author}
              {caption && <LightboxCaption key={caption} text={caption} maxChars={maxChars} />}
              {actions}
            </div>
          )}
          {footer}
        </div>
      )}
      {comments && (
        <div
          className="flex-1 min-h-0 w-full bg-white rounded-t-[1.25rem] overflow-hidden shadow-[0_-8px_30px_rgba(0,0,0,0.35)] animate-in slide-in-from-bottom-8 fade-in duration-300"
          style={{ paddingBottom: "var(--sa-bottom, 0px)" }}
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-comments"
        >
          {comments}
        </div>
      )}
    </div>,
    document.body
  );
};

/** Facebook-style photo mosaic inside a post (1..5+ with +N overflow). */
// Photo seule : la place est réservée d'après ses dimensions (zéro saut), ratio borné 4:5 … 1.91:1 (rendu régulier).
// La valeur est exposée en variable CSS pour que le PC applique sa propre borne (voir SINGLE_BOX).
const singlePhotoStyle = (dims, url) => {
  const d = dims?.[url];
  if (!Array.isArray(d) || !(d[0] > 0) || !(d[1] > 0)) return undefined;
  return { "--ph-ratio": String(Math.min(1.91, Math.max(0.8, d[0] / d[1]))) };
};
// Téléphone : cadre 4:5 … 1.91:1 plein (inchangé). PC — règle Facebook : hauteur ≤ 80 % de la largeur de la carte
// (ratio ≥ 1.25) et ≤ 70 % de l'écran, photo ENTIÈRE (object-contain) sur fond sombre → bandes latérales pour les portraits.
const SINGLE_BOX = "relative overflow-hidden block w-full bg-black/5 md:bg-[#111418] md:max-h-[70vh]";
const SINGLE_RATIO = "aspect-[var(--ph-ratio)] md:aspect-[max(var(--ph-ratio),1.25)]";

// Largeur CSS d'une carte du fil (≤ 640 px) : la photo seule la remplit, une vignette de mosaïque en occupe la moitié.
const cardWidth = () => Math.min(typeof window !== "undefined" ? window.innerWidth : 640, 640);

export const PostPhotos = ({ images = [], dims, onOpen }) => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  if (!images || images.length === 0) return null;
  const openAt = (i) => { if (onOpen) { onOpen(i); return; } setIndex(i); setOpen(true); };
  const n = images.length;

  const Tile = ({ i, className, overlay }) => (
    <button
      type="button"
      onClick={() => openAt(i)}
      className={`relative overflow-hidden bg-black/5 ${className}`}
      data-testid={`post-photo-${i}`}
    >
      <img src={imageVariant(images[i], cardWidth() / 2)} loading="lazy" decoding="async" alt="" className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.04]" />
      {overlay != null && (
        <div className="absolute inset-0 bg-black/55 grid place-items-center">
          <span className="text-white text-2xl font-bold font-display">+{overlay}</span>
        </div>
      )}
    </button>
  );

  let grid;
  if (n === 1) {
    const style = singlePhotoStyle(dims, images[0]);
    grid = (
      <button type="button" onClick={() => openAt(0)} className={`${SINGLE_BOX} ${style ? SINGLE_RATIO : ""}`} style={style} data-testid="post-photo-0">
        <img src={imageVariant(images[0], 64)} loading="lazy" decoding="async" alt="" aria-hidden data-testid="post-photo-0-blur"
          className="absolute inset-0 hidden md:block w-full h-full object-cover pointer-events-none" style={{ filter: "blur(28px)", transform: "scale(1.18)" }} />
        <img src={imageVariant(images[0], cardWidth(), { cssHeight: singlePhotoHeight(style, cardWidth()) })} loading="lazy" decoding="async" alt="" className={`relative w-full object-cover md:object-contain ${style ? "h-full" : "max-h-[520px] md:max-h-[70vh]"}`} />
      </button>
    );
  } else if (n === 2) {
    grid = (
      <div className="grid grid-cols-2 gap-1">
        <Tile i={0} className="aspect-[3/4]" />
        <Tile i={1} className="aspect-[3/4]" />
      </div>
    );
  } else if (n === 3) {
    grid = (
      <div className="grid grid-cols-2 grid-rows-2 gap-1 h-[340px]">
        <Tile i={0} className="row-span-2 h-full" />
        <Tile i={1} className="h-full" />
        <Tile i={2} className="h-full" />
      </div>
    );
  } else if (n === 4) {
    grid = (
      <div className="grid grid-cols-2 gap-1">
        {[0, 1, 2, 3].map((i) => <Tile key={i} i={i} className="aspect-square" />)}
      </div>
    );
  } else {
    grid = (
      <div className="grid grid-cols-6 gap-1">
        <Tile i={0} className="col-span-3 aspect-[16/10]" />
        <Tile i={1} className="col-span-3 aspect-[16/10]" />
        <Tile i={2} className="col-span-2 aspect-square" />
        <Tile i={3} className="col-span-2 aspect-square" />
        <Tile i={4} className="col-span-2 aspect-square" overlay={n > 5 ? n - 5 : null} />
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl overflow-hidden" style={{ border: "1px solid var(--yalola-border)" }}>
      {grid}
      {open && <Lightbox images={images} index={index} onClose={() => setOpen(false)} onIndex={setIndex} />}
    </div>
  );
};

/** Uniform square album grid (profile Photos tab). */
export const PhotoAlbumGrid = ({ images = [] }) => {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  if (!images || images.length === 0) {
    return (
      <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="photos-empty">
        {t("photos.empty", "Aucune photo pour le moment.")}
      </div>
    );
  }
  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="photo-album">
        {images.map((img, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setIndex(i); setOpen(true); }}
            className="relative aspect-square overflow-hidden rounded-lg bg-black/5"
            data-testid={`album-photo-${i}`}
          >
            <img src={imageVariant(img, cardWidth() / 3)} loading="lazy" decoding="async" alt="" className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
          </button>
        ))}
      </div>
      {open && <Lightbox images={images} index={index} onClose={() => setOpen(false)} onIndex={setIndex} />}
    </>
  );
};
